// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { useState, useMemo, useEffect } from 'react';
import { Search, Inbox, Calendar, Building2, Users } from 'lucide-react';
import { paletteEntriesFor } from '../constants/commandpalette';
import { isTrimmedForMoments } from '../lib/accessGating';
import { searchShape } from '../lib/searchShape';

const GROUP_ORDER = ['find', 'page', 'action', 'help'];
const GROUP_LABELS = { find: 'Find', page: 'Go to', action: 'Create', help: 'Help' };

/**
 * WHAT WAS TYPED, WHEN IT NAMES SOMEONE'S ORDER RATHER THAN A PAGE.
 *
 * Support starts with a caller reading out a phone number, an email, or the code on their SMS. The
 * palette knew only pages, so the first thing an agent typed returned "No results". It now names
 * what the text is and offers the one search that answers it — opening Orders with the value in
 * the box and the right field chosen. It never queries on its own; the agent chooses the search.
 *
 * The shapes live in lib/searchShape — the same rule an orders search box uses to pick its field.
 */
const recognize = searchShape;
const findItem = ({ qf, q, what, shown }) => ({
  kind: 'find', id: 'orders', search: { q, qf }, icon: Inbox,
  label: `Search orders by ${what}`, sub: shown,
});

export const CommandPalette = ({ open, onClose, onNavigate, onAction, userEmail }) => {
  // The sidebar's trimming, applied here too: what the sidebar hides, ⌘K does not offer.
  const entries = useMemo(() => paletteEntriesFor(isTrimmedForMoments(userEmail)), [userEmail]);
  // ONE way an entry runs, for Enter and for a click alike: a link outside the app opens in a new tab, a page or a
  // search navigates, anything else is an action.
  const run = (item) => {
    if (!item) return;
    if (item.href) window.open(item.href, '_blank', 'noopener');
    else if (item.kind === 'page' || item.kind === 'find') onNavigate(item.id, item.search);
    else onAction(item.action);
    onClose();
  };
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  // Adjust state during render (not in an effect) when `open` changes, so the
  // palette never flashes the previous session's query for a frame on reopen —
  // see react.dev "Adjusting some state when a prop changes".
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) { setQuery(''); setCursor(0); }
  }

  const filtered = useMemo(() => {
    const available = entries;
    if (!query.trim()) return available;
    const q = query.trim().toLowerCase();
    if (!q) return available;
    const found = recognize(query);
    if (found) return [findItem(found)];
    // Every word must appear somewhere (label, where it lives, or its synonyms), in any order —
    // "sales affiliates" and "affiliate" both find Sales Setup → Affiliates. Label hits rank first.
    const words = q.split(/\s+/);
    const hay = (e) => `${e.label} ${e.sub || ''} ${e.keywords || ''}`.toLowerCase();
    const matches = available
      .filter((e) => { const h = hay(e); return words.every((w) => h.includes(w)); })
      .map((e) => { const l = e.label.toLowerCase(); return { e, rank: l.startsWith(q) ? 0 : words.every((w) => l.includes(w)) ? 1 : 2 }; })
      // Grouped first, ranked within: the list is DRAWN by group, and Enter takes filtered[cursor] —
      // ranked across groups, the highlighted row and the row Enter opened were different rows.
      .sort((a, b) => (GROUP_ORDER.indexOf(a.e.kind) - GROUP_ORDER.indexOf(b.e.kind)) || (a.rank - b.rank))
      .map((x) => x.e);
    // A NAME: what an operator calls an event, an organizer or a buyer ("Kitara", "Blacks Power"). Each is offered as the
    // search its own page runs — the palette still queries nothing itself; the operator picks which thing they meant.
    const text = query.trim();
    const named = text.length >= 2 && /^[\p{L}\p{N}' .&-]+$/u.test(text) ? [
      { kind: 'find', id: 'events', search: { q: text }, icon: Calendar, label: `Find events named “${text}”`, sub: 'Events · past and upcoming' },
      { kind: 'find', id: 'organizers', search: { q: text }, icon: Building2, label: `Find organizers named “${text}”`, sub: 'Organizers' },
      { kind: 'find', id: 'people', search: { tab: 'customers', q: text }, icon: Users, label: `Find customers named “${text}”`, sub: 'People · customers' },
      findItem({ qf: 'name', q: text, what: 'buyer name', shown: text }),
    ] : [];
    return [...matches, ...named];
  }, [query, entries]);

  useEffect(() => { if (cursor >= filtered.length) setCursor(0); }, [filtered.length, cursor]);

  // Keyboard nav inside palette
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, filtered.length - 1)); }
      else if (e.key === 'ArrowUp')   { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
      else if (e.key === 'Enter') {
        e.preventDefault();
        run(filtered[cursor]);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, filtered, cursor, onClose, onNavigate, onAction]);

  if (!open) return null;
  const groups = filtered.reduce((acc, it) => { (acc[it.kind] = acc[it.kind] || []).push(it); return acc; }, {});

  // Map flat index per item for cursor highlighting
  let flatIdx = -1;

  return (
    <div className="fixed inset-0 z-[100] bg-[#1F2937]/40 flex items-start justify-center pt-[12vh] px-4" onClick={onClose}>
      <div className="bg-white border border-[#E6E4E1] rounded-[12px] w-full max-w-[600px] shadow-2xl overflow-hidden flex flex-col max-h-[70vh]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#EEEDEA]">
          <Search className="w-4 h-4 text-[#9CA3AF]" strokeWidth={2} />
          <input
            autoFocus
            value={query}
            onChange={(e) => { setQuery(e.target.value); setCursor(0); }}
            placeholder="Search"
            className="flex-1 bg-transparent text-[13.5px] text-[#1F2937] placeholder:text-[#9CA3AF] focus:outline-none"
          />
          <kbd className="text-[10px] text-[#9CA3AF] bg-[#F7F6F2] border border-[#E6E4E1] rounded px-1.5 py-0.5 font-mono">ESC</kbd>
        </div>

        <div className="flex-1 overflow-y-auto py-1">
          {filtered.length === 0 && (
            <div className="px-4 py-10 text-center text-[12.5px] text-[#9CA3AF]">No results for "{query}"</div>
          )}
          {GROUP_ORDER.map(g => {
            if (!groups[g]) return null;
            return (
              <div key={g} className="py-1">
                <div className="px-4 pt-2 pb-1 text-[10px] font-semibold tracking-[0.06em] uppercase text-[#9CA3AF]">{GROUP_LABELS[g]}</div>
                {groups[g].map((it) => {
                  flatIdx++;
                  const active = flatIdx === cursor;
                  const Icon = it.icon;
                  return (
                    <button
                      key={`${it.kind}:${it.id || it.action}:${JSON.stringify(it.search || {})}:${it.label}`}
                      onClick={() => run(it)}
                      onMouseEnter={() => setCursor(flatIdx)}
                      className={`w-full text-left px-4 py-2 flex items-center gap-3 ${active ? 'bg-[#F2F1ED]' : 'hover:bg-[#FBFAF7]'}`}
                    >
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${active ? 'bg-white border border-[#E6E4E1]' : 'bg-[#FBFAF7] border border-[#EEEDEA]'}`}>
                        {Icon && <Icon className="w-3.5 h-3.5 text-[#4B5563]" strokeWidth={1.75} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] font-medium text-[#1F2937] leading-tight">{it.label}</div>
                        {it.sub && <div className="text-[10.5px] text-[#6B7280] mt-0.5">{it.sub}</div>}
                      </div>
                      {it.keys && (
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          {it.keys.split(' ').map((k, i) => (
                            <kbd key={i} className="text-[10px] text-[#6B7280] bg-[#FBFAF7] border border-[#E6E4E1] rounded px-1.5 py-0.5 font-mono uppercase">{k}</kbd>
                          ))}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div className="px-4 py-2 border-t border-[#EEEDEA] bg-[#FBFAF7] text-[10.5px] text-[#6B7280] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span><kbd className="bg-white border border-[#E6E4E1] rounded px-1 font-mono">↑↓</kbd> navigate</span>
            <span><kbd className="bg-white border border-[#E6E4E1] rounded px-1 font-mono">↵</kbd> select</span>
            <span><kbd className="bg-white border border-[#E6E4E1] rounded px-1 font-mono">esc</kbd> close</span>
          </div>
          <span>{filtered.length} of {entries.length}</span>
        </div>
      </div>
    </div>
  );
};
